/**
 * Scenario Comparison - Compare multiple what-if scenarios
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Compare as CompareIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import type { ScenarioResponse, ScenarioComparison as ScenarioComparisonType, QuickInsights } from '../../types/scenarios';

const ScenarioComparison: React.FC = () => {
  const { fmtCurrency } = useCurrencyFormat();
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [comparison, setComparison] = useState<ScenarioComparisonType | null>(null);
  const [quickInsights, setQuickInsights] = useState<QuickInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadScenarios();
    loadQuickInsights();
  }, []);

  const loadScenarios = async () => {
    setLoadingScenarios(true);
    try {
      const response = await api.getAllScenarios();
      setScenarios(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error loading scenarios');
    } finally {
      setLoadingScenarios(false);
    }
  };

  const loadQuickInsights = async () => {
    try {
      const response = await api.getQuickInsights();
      setQuickInsights(response.data);
    } catch (err: any) {
      // Error handled by centralized API error emitter
    }
  };

  const handleScenarioSelect = (scenarioId: string) => {
    setSelectedScenarios((prev) => {
      if (prev.includes(scenarioId)) {
        return prev.filter((id) => id !== scenarioId);
      } else if (prev.length < 5) {
        return [...prev, scenarioId];
      }
      return prev;
    });
  };

  const handleCompare = async () => {
    if (selectedScenarios.length < 2) {
      setError('Please select at least 2 scenarios to compare');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.compareScenarios({
        scenario_ids: selectedScenarios,
        comparison_metrics: ['cost', 'service_level', 'stockout_risk'],
      });
      setComparison(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error comparing scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    try {
      await api.deleteScenario(scenarioId);
      setScenarios((prev) => prev.filter((s) => s.scenario_id !== scenarioId));
      setSelectedScenarios((prev) => prev.filter((id) => id !== scenarioId));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error deleting scenario');
    }
  };

  const getScenarioTypeColor = (type: string) => {
    const colors: Record<string, any> = {
      sensitivity: 'primary',
      demand: 'success',
      disruption: 'error',
      budget: 'warning',
      service_level: 'info',
    };
    return colors[type] || 'default';
  };

  // Prepare radar chart data
  const prepareRadarData = () => {
    if (!comparison) return [];

    const metrics = ['cost', 'service_level', 'stockout_risk'];
    return metrics.map((metric) => {
      const dataPoint: any = { metric: metric.replace('_', ' ').toUpperCase() };
      comparison.scenarios.forEach((scenario, index) => {
        const value = scenario[metric as keyof typeof scenario];
        // Normalize values to 0-100 scale
        let normalizedValue = 0;
        if (metric === 'service_level') {
          normalizedValue = typeof value === 'number' ? value : 0;
        } else if (metric === 'stockout_risk') {
          normalizedValue = typeof value === 'number' ? 100 - value : 0; // Invert so lower is better
        } else if (metric === 'cost') {
          const maxCost = Math.max(...comparison.scenarios.map((s) => s.total_cost));
          normalizedValue = typeof value === 'number' ? ((maxCost - value) / maxCost) * 100 : 0; // Invert so lower is better
        }
        dataPoint[`Scenario ${index + 1}`] = normalizedValue;
      });
      return dataPoint;
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Scenario Comparison
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', mt: 0.5 }}>
            Compare multiple scenarios to find the best approach
          </Typography>
        </Box>
        <Tooltip title="Refresh scenarios">
          <IconButton onClick={loadScenarios} disabled={loadingScenarios} sx={{ color: 'var(--color-text-muted)' }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Quick Insights Card */}
      {quickInsights && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, mb: 2 }}>Quick Insights</Typography>
            <Grid container spacing={2}>
              {quickInsights.insights.demand_spike_20_percent && (
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      20% Demand Spike Impact
                    </Typography>
                    <Typography variant="body2">
                      Stockout Risk: <strong>{quickInsights.insights.demand_spike_20_percent.stockout_risk.toFixed(1)}%</strong>
                    </Typography>
                    <Typography variant="body2">
                      Additional Inventory Needed: <strong>{quickInsights.insights.demand_spike_20_percent.additional_inventory_needed}</strong>
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {quickInsights.insights.supplier_delay_7_days && (
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      7-Day Supplier Delay
                    </Typography>
                    <Typography variant="body2">
                      Stockout Days: <strong>{quickInsights.insights.supplier_delay_7_days.estimated_stockout_days}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Revenue at Risk: <strong>{fmtCurrency(quickInsights.insights.supplier_delay_7_days.revenue_at_risk)}</strong>
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {quickInsights.insights.service_level_95_cost && (
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Optimal Service Level
                    </Typography>
                    <Typography variant="body2">
                      Target: <strong>{quickInsights.insights.service_level_95_cost.optimal_service_level.toFixed(1)}%</strong>
                    </Typography>
                    <Typography variant="body2">
                      Cost: <strong>{fmtCurrency(quickInsights.insights.service_level_95_cost.optimal_cost)}</strong>
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Saved Scenarios List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Saved Scenarios ({scenarios.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Select 2-5 scenarios to compare
              </Typography>

              {loadingScenarios ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : scenarios.length === 0 ? (
                <Alert severity="info">
                  No saved scenarios. Create scenarios in the Scenario Builder to compare them here.
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell>Scenario</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scenarios.map((scenario) => (
                        <TableRow key={scenario.scenario_id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedScenarios.includes(scenario.scenario_id)}
                              onChange={() => handleScenarioSelect(scenario.scenario_id)}
                              disabled={!selectedScenarios.includes(scenario.scenario_id) && selectedScenarios.length >= 5}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{scenario.scenario_name}</Typography>
                            {scenario.description && (
                              <Typography variant="caption" color="text.secondary">
                                {scenario.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={scenario.scenario_type} size="small" color={getScenarioTypeColor(scenario.scenario_type)} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleDeleteScenario(scenario.scenario_id)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <CompareIcon />}
                onClick={handleCompare}
                disabled={selectedScenarios.length < 2 || loading}
                sx={{ mt: 2 }}
              >
                Compare Selected ({selectedScenarios.length})
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Comparison Results */}
        <Grid item xs={12} md={8}>
          {comparison ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comparison Results
                </Typography>

                {/* Best Scenarios Summary */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: 'Best Cost', id: comparison.best_cost_scenario_id },
                    { label: 'Best Service Level', id: comparison.best_service_level_scenario_id },
                    { label: 'Best Overall', id: comparison.best_overall_scenario_id },
                  ].map((item) => (
                    <Grid item xs={12} md={4} key={item.label}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                          {comparison.scenarios.find((s) => s.scenario_id === item.id)?.scenario_name || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Radar Chart Comparison */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Multi-Metric Comparison
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={prepareRadarData()}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      <RechartsTooltip />
                      <Legend />
                      {comparison.scenarios.map((_, index) => (
                        <Radar
                          key={index}
                          name={`Scenario ${index + 1}`}
                          dataKey={`Scenario ${index + 1}`}
                          stroke={['#8B2438', '#C9A96E', '#16A34A', '#DC2626', '#0891B2'][index]}
                          fill={['#8B2438', '#C9A96E', '#16A34A', '#DC2626', '#0891B2'][index]}
                          fillOpacity={0.3}
                        />
                      ))}
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Metrics Comparison Table */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Scenario</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">Service Level</TableCell>
                        <TableCell align="right">Stockout Risk</TableCell>
                        <TableCell align="right">Inventory Level</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.scenarios.map((scenario) => (
                        <TableRow key={scenario.scenario_id}>
                          <TableCell>
                            <Typography variant="body2">{scenario.scenario_name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={scenario.scenario_type} size="small" color={getScenarioTypeColor(scenario.scenario_type)} />
                          </TableCell>
                          <TableCell align="right">{fmtCurrency(scenario.total_cost)}</TableCell>
                          <TableCell align="right">{scenario.service_level.toFixed(1)}%</TableCell>
                          <TableCell align="right">{scenario.stockout_risk.toFixed(1)}%</TableCell>
                          <TableCell align="right">{scenario.inventory_level.toFixed(0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Insights */}
                {comparison.insights && comparison.insights.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Key Insights
                    </Typography>
                    {comparison.insights.map((insight, index) => (
                      <Alert key={index} severity="info" sx={{ mb: 1 }}>
                        {insight}
                      </Alert>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--color-text-muted)', mb: 1 }}>
                    No Comparison Selected
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-disabled)' }}>
                    Select 2-5 scenarios from the list and click "Compare Selected" to see detailed comparison
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScenarioComparison;
